/** The visit card's frame — layout, tile, collapse, drag (moved from `builder/rows.tsx` at step 8). */
import { useState, type ReactNode } from 'react'
import { cn } from '../../lib/cn.js'
import { IconGrip } from '../../frame/icons.js'
import { RowMenuButton } from './add-row.js'
import { CollapseCaret } from './caret.js'

// ---------------------------------------------------------------------------
// The visit
// ---------------------------------------------------------------------------

export interface VisitShellProps {
  /** The 40px tile, drawn by the host — it knows what kind of trip this is. */
  tile: ReactNode
  name: string
  onEditName?: () => void
  /** "auto-named", the role tag, "Optional". */
  tags?: ReactNode
  /**
   * The row under the name.
   *
   * A quote puts its three facts here — schedule, billing plan, notes — each a
   * button onto the part of the editor that owns it. A package puts its When
   * row here. Neither is the other's business.
   */
  meta?: ReactNode
  /** "× 12 visits", or nothing. */
  qty?: ReactNode
  /** The totals block at the right. */
  right?: ReactNode
  selected?: boolean
  collapsed?: boolean
  /** What a collapsed card still says, in one line. */
  summary?: string
  onSelect?: () => void
  onToggle?: () => void
  onMenu?: (at: { x: number; y: number }) => void
  /**
   * Reordering by dragging the grip.
   *
   * The grip was `aria-hidden` with no handler at all — a cursor that said
   * "grab me" over something that could not be grabbed. Given `onDrop`, the
   * card becomes a real drag source and target; without it the grip is not
   * drawn, because a handle that does nothing is worse than no handle.
   */
  dragIndex?: number
  onDrop?: (from: number, to: number) => void
  /** The "+ Add service" strip under the children. */
  footer?: ReactNode
  children?: ReactNode
}

/** The card being dragged, for the drop target to read. */
const DRAG_TYPE = 'application/x-rowted-visit'

export function VisitShell({
  tile, name, onEditName, tags, meta, qty, right,
  selected, collapsed, summary, onSelect, onToggle, onMenu, dragIndex, onDrop,
  footer, children,
}: VisitShellProps) {
  const draggable = onDrop !== undefined && dragIndex !== undefined
  const [over, setOver] = useState(false)

  return (
    <section
      onFocus={onSelect}
      onDragOver={draggable
        ? (event) => { event.preventDefault(); setOver(true) }
        : undefined}
      onDragLeave={draggable ? () => setOver(false) : undefined}
      onDrop={draggable
        ? (event) => {
          event.preventDefault()
          setOver(false)
          const from = Number(event.dataTransfer.getData(DRAG_TYPE))
          if (Number.isInteger(from) && from !== dragIndex) onDrop(from, dragIndex)
        }
        : undefined}
      className={cn(
        'overflow-hidden rounded-record border bg-surface',
        selected ? 'border-accent-line ring-2 ring-accent-ring' : 'border-line',
        over && 'border-accent-line ring-2 ring-accent-ring',
      )}
    >
      <header className="flex items-center gap-3 px-3.5 py-3">
        {draggable && (
          <span
            draggable
            aria-hidden
            data-drag="visit"
            onDragStart={(event) => {
              event.dataTransfer.setData(DRAG_TYPE, String(dragIndex))
              event.dataTransfer.effectAllowed = 'move'
            }}
            className="cursor-grab text-line-strong active:cursor-grabbing"
          >
            <IconGrip size={16} />
          </span>
        )}

        {/*
          * The caret, then the tile a little in from it (step 21 item 4.4):
          * every box in the builder folds from the same place.
          */}
        {onToggle && <CollapseCaret collapsed={collapsed ?? false} onToggle={onToggle} label={name} />}
        <span className="ml-0.5 flex shrink-0">{tile}</span>

        <div className="flex min-w-0 flex-col gap-[3px]">
          <span className="flex items-center gap-2">
            {onEditName
              ? (
                <button
                  type="button"
                  onClick={onEditName}
                  className="truncate text-[15px] font-bold text-text hover:text-accent"
                >
                  {name}
                </button>
              )
              : <b className="truncate text-[15px] font-bold text-text">{name}</b>}
            {tags}
          </span>
          {meta && (
            <span className={cn('flex flex-wrap items-center gap-x-3.5 gap-y-1 text-body',
              'text-text-muted')}>
              {meta}
            </span>
          )}
        </div>

        <div className="ml-auto flex items-center gap-[18px]">
          {qty}
          {right}
          {onMenu && <RowMenuButton label={`${name} options`} onOpen={onMenu} />}
        </div>
      </header>

      {collapsed
        ? (
          /* What a folded visit still says. The caret opens it again; the old foot-of-card Expand link is gone. */
          <div className={cn('flex items-center gap-2 border-t border-line bg-surface-alt',
            'px-3.5 py-2.5 text-body text-text-muted')}>
            <span className="min-w-0 truncate">{summary}</span>
          </div>
        )
        : (
          <>
            {children && <div className="border-t border-line">{children}</div>}
            {footer && (
              <div className={cn('flex items-center gap-2 border-t border-line bg-surface-alt',
                'py-2 pl-6 pr-3.5')}>
                {footer}
              </div>
            )}
          </>
        )}
    </section>
  )
}
